import { type ReactNode, useEffect, useRef } from "react";

import { useApp } from "@entities/app";
import { useSpinnerOverlay } from "@shared/ui/chakra/hooks";

type Props = {
  children?: ReactNode;
};

const MIN_SHOW_TIME = 500;
const DELAY_BEFORE_SHOW = 200;

export const StartupGuard = ({ children }: Props) => {
  const { isInitialized } = useApp();
  const { close, open } = useSpinnerOverlay("startup-initialization");
  const openTimeRef = useRef<null | number>(null);

  useEffect(() => {
    if (isInitialized && openTimeRef.current !== null) {
      const displayedTime = Date.now() - openTimeRef.current;
      const remainingTime = Math.max(0, MIN_SHOW_TIME - displayedTime);

      const timer = setTimeout(() => {
        close();
        openTimeRef.current = null;
      }, remainingTime);

      return () => clearTimeout(timer);
    }

    if (isInitialized) {
      return;
    }

    const timer = setTimeout(() => {
      open();
      openTimeRef.current = Date.now();
    }, DELAY_BEFORE_SHOW);

    return () => {
      clearTimeout(timer);
    };
  }, [close, isInitialized, open]);

  if (!isInitialized) {
    return null;
  }

  return <>{children}</>;
};
